import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardActions,
    Grid,
    CircularProgress,
    Tooltip,
    useTheme,
    Button,
    Collapse
} from '@mui/material';
import axios from 'axios';
import ArticleIcon from '@mui/icons-material/Article';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import DOMPurify from 'dompurify';
import api from "../axios/axiosInstance";

const CurrentBlogs = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({}); // Track expansion state per blog
    const theme = useTheme();

    useEffect(() => {
        api
            .get(`${process.env.REACT_APP_BACKEND_URL}/grades/blog-list/`)
            .then((res) => setBlogs(res.data))
            .catch((err) => console.error('❌ Failed to fetch blogs:', err))
            .finally(() => setLoading(false));
    }, []);

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const toggleExpand = (index) => {
        setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const truncateHtml = (html = '', wordLimit = 100) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        const words = div.textContent.split(' ');
        return words.length > wordLimit
            ? words.slice(0, wordLimit).join(' ') + '...'
            : div.textContent;
    };

    return (
        <Box sx={{ mt: 8, px: { xs: 2, sm: 4 } }}>
            <Typography
                variant="h4"
                gutterBottom
                fontWeight="bold"
                color={theme.palette.primary.main}
            >
                📰 Latest Blog Posts
            </Typography>

            {loading ? (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={4} direction="column">
                    {blogs.map((post, idx) => {
                        const isExpanded = expanded[idx] || false;
                        const sanitizedHTML = DOMPurify.sanitize(post.blog_details);
                        const preview = truncateHtml(post.blog_details, 100);

                        return (
                            <Grid item xs={12} key={idx}>
                                <Card
                                    sx={{
                                        borderRadius: 3,
                                        boxShadow: 6,
                                        bgcolor: theme.palette.background.paper,
                                    }}
                                >
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            gutterBottom
                                            sx={{
                                                fontSize: '1.2rem',
                                                color: theme.palette.secondary.main,
                                            }}
                                        >
                                            <ArticleIcon sx={{ mr: 1, fontSize: 18, verticalAlign: 'middle' }} />
                                            {post.topic}
                                        </Typography>

                                        {post.sub_topic && (
                                            <Typography
                                                variant="subtitle2"
                                                color="text.secondary"
                                                sx={{ fontStyle: 'italic', mb: 1 }}
                                            >
                                                {post.sub_topic}
                                            </Typography>
                                        )}

                                        <Collapse in={isExpanded} collapsedSize={90}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                component="div"
                                                dangerouslySetInnerHTML={{
                                                    __html: isExpanded ? sanitizedHTML : DOMPurify.sanitize(preview),
                                                }}
                                            />
                                        </Collapse>

                                        <Button
                                            size="small"
                                            onClick={() => toggleExpand(idx)}
                                            sx={{ mt: 1 }}
                                        >
                                            {isExpanded ? 'Show Less' : 'Read More'}
                                        </Button>
                                    </CardContent>

                                    <CardActions
                                        sx={{
                                            px: 2,
                                            pb: 2,
                                            pt: 0,
                                            justifyContent: 'space-between',
                                            borderTop: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon fontSize="small" />
                                            <Tooltip title="Author">
                                                <Typography variant="caption">{post.name}</Typography>
                                            </Tooltip>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CalendarMonthIcon fontSize="small" />
                                            <Tooltip title="Date Posted">
                                                <Typography variant="caption">
                                                    {formatDate(post.date_created)}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    </CardActions>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Box>
    );
};

export default CurrentBlogs;
